/*
=============================================
   PER-VIEW HELP - CONTENT
=============================================
⚠️ DRAFT 2026-08-29. Wired to NOTHING yet. This file is the proposed SHAPE for
David to react to before shell.js or guide.html are touched. Importing it has no
effect; nothing reads it.

--------------------------------------------------------------------------
WHY THIS IS A DATA FILE AND NOT PROSE IN TWELVE COMPONENTS
--------------------------------------------------------------------------
Two surfaces render from this one file:

   1. the collapsible "What is this?" panel at the top of each view
   2. the matching per-tab section in guide.html

That is the same arrangement training-content.js already uses for the Training
tab and /training.html, whose header says it plainly: both render from one file
"so the two can never drift apart."

⭐ THE POINT WORTH KEEPING. README.md carries a standing rule: a change that
alters what a user can see or do updates guide.html in the SAME commit. Today
that depends on somebody REMEMBERING. Once the tab panel and the guide both
render from here, changing what a tab does forces you through the single file
that feeds both, and the rule becomes a property of the code rather than a
discipline. That is the main argument for this design over writing the help
inline in each component.

--------------------------------------------------------------------------
SHAPE
--------------------------------------------------------------------------
Keyed by the view name in `data-view="..."`, so a panel can be injected from
one place in the shell instead of edited into a dozen components.

   title    short, and NOT the tab name repeated back. "Modules" is useless as
            a heading above the Modules tab. Say what it is FOR.
   blurb    one sentence. What this screen is for. Shown collapsed AND expanded,
            so it has to stand alone.
   points   2 to 4 bullets. The things people actually get wrong, not a feature
            tour. If a bullet is not something someone has misunderstood, cut it.
   more     optional. { text, href } deep link into guide.html for the long
            version, so the panel never has to grow into an essay.

RULES FOR WRITING THESE
   - No em-dashes. American spellings. Same as every other deliverable.
   - Say what happens, not what to click. The screen already shows the buttons.
   - A warning earns its place only if getting it wrong costs something real
     (an answer key leaking, a grade lost). Otherwise leave it out.

OPEN QUESTIONS FOR DAVID, deliberately not decided here:
   a) Default expanded on first visit, then remembered per view in localStorage?
      A professor in week 10 should not re-collapse this every day. Wrapped in
      try/catch so a browser blocking storage just shows it expanded.
   b) Should students see panels at all, or only professors? Drafted for both
      below, since student.html has the same data-view structure and the student
      views are arguably where it matters more.
   c) Does every view need one? `profile` almost certainly does not. Views with
      no entry here simply render no panel, which is the intended degrade.
*/

export const HELP = {

   /* ---------------- shared: professor + admin ---------------- */

   classes: {
      title: "Your classes and rosters",
      blurb: "Every class you own, with its roster and the assignments attached to it.",
      points: [
         "A student cannot sign in until they are on a roster here. The error they report is that their email is not recognized.",
         "Enroll people by the address they will actually sign in with. A mismatch is the single most common support question.",
         "The role you give someone applies to THIS class only, so a club officer can be a TA here and a student elsewhere.",
      ],
   },

   assignments: {
      title: "The assignment library",
      blurb: "Every assignment on the platform, and where you build a team version of one.",
      points: [
         "An assignment is the graded work itself: a workbook with questions and an answer key.",
         "Attaching one to a class gives students that piece of work on its own. To hand over supporting material with it, use a module.",
      ],
      more: { text: "Assignments vs modules", href: "guide.html#vs" },
   },

   "skill-map": {
      title: "What every position should be able to do",
      blurb: "The team's skills by area. Each one is Foundational (tested for every player) or a Position skill, with a target tier per position.",
      points: [
         "A skill stays To review, and is not tested, until its scope is decided and saved.",
         "Importance 1 means awareness only: a position rated 1 is never tested on that skill.",
         "No tab yet means nothing in the library can test the skill. Practice to build lists those, and case breakdowns say where to build them from.",
         "Priority (now, soon, later) orders each player's gap list in the Game Launcher. Requests holds what the coaches asked for; your reply shows on their page.",
      ],
   },

   "class-readiness": {
      title: "Who is ready for their position",
      blurb: "Each player against every Foundational skill plus their position's skills, at the target tiers on the Skill map.",
      points: [
         "Met means a clean clear of a Game Launcher tab carrying the skill at the target tier. Tabs cleared with the guide open do not count.",
         "Without a position, a player is checked on Foundational skills only. Set it in the Position column.",
         "The suggested pack is one already attached to this class; if none trains the skill at the right tier, the row says so.",
         "Coaching: set this week's focus, suggest packs with a due date, and ask Coach Brown for more practice. Players see the focus and your suggestions in the Game Launcher, above Next up.",
      ],
   },

   modules: {
      title: "Assignments plus the material that goes with them",
      blurb: "A module groups an assignment with its resources, then pours the whole set into a class in one action.",
      points: [
         "Resources are never graded. They are solutions PDFs, worked files, teaching guides and video links.",
         "Every resource starts as Instructors only. An answer key should never become visible because a field was left alone.",
         "Access is worked out per class, per person. Adding a module to another class never widens who can see what.",
         "Two sections sitting the same assessment on different days need a per-class release, or the later one gets the answers early.",
      ],
      more: { text: "Building a module", href: "guide.html#build" },
   },

   scoreboard: {
      title: "The live ranked board",
      blurb: "A leaderboard for one class and assignment, updating as answers come in.",
      points: [
         "Generate an overlay link to put the board into OBS as a Browser source.",
         "Regenerating that link mints a fresh URL and kills the old one, so you can hand out a clean link safely.",
      ],
      more: { text: "Putting it on a stream", href: "guide.html#obs" },
   },

   games: {
      title: "Running a timed session",
      blurb: "Release a file to a class, then start and stop the clock on a live competition.",
      points: [
         "Releasing the file and starting the clock are two separate actions, so students can open the workbook before the timer runs.",
         "A game cannot be deleted while it is open. Stop it first.",
      ],
   },

   activity: {
      title: "What students actually did",
      blurb: "The per-student work log the add-in records while the task pane is open.",
      points: [
         "Nothing is recorded when the pane is closed, so a gap usually means the pane was shut, not that the student stopped.",
      ],
   },

   engagement: {
      title: "Time on task across a class",
      blurb: "Credited active time per student, against whatever minimum the course sets.",
      points: [
         "Active time is not elapsed time. A file open for two hours with no work in it earns very little.",
         "Students see their own figures on My time, so these numbers should match what they are looking at.",
      ],
   },

   export: {
      title: "Getting grades out",
      blurb: "Downloads the gradebook for a class as a CSV.",
      points: [
         "Scores come from the server's stored answer key, not from anything in the student's copy of the workbook.",
      ],
   },

   convert: {
      title: "Turning your own file into an assignment",
      blurb: "Takes an existing Excel file and produces an add-in-ready version with the answers stripped out.",
      points: [
         "The converter decides server grading and parameter handling from what it finds in the file. Those are not free choices.",
         "A converted file is not the file you uploaded. Distribute the converted one.",
      ],
   },

   /* ---------------- admin only ---------------- */

   roles: {
      title: "Who can open what",
      blurb: "The global role decides which screens a person can reach at all.",
      points: [
         "This is NOT the same as a class role. A TA seat is granted inside a class, and is the safe way to give a student leader real access to one class without making them a professor of everything.",
      ],
   },

   /* ---------------- student ---------------- */

   "student:classes": {
      title: "Your classes and what is due",
      blurb: "Every class you are enrolled in, with its assignments and any resources your instructor has released.",
      points: [
         "Resources appear only after your instructor releases them, so a section that is not there yet is not missing.",
         "Open assignments in Excel with the task pane open, or your work is not recorded.",
      ],
      more: { text: "Setting up the add-in", href: "setup.html" },
   },

   "student:my-time": {
      title: "How much you have worked",
      blurb: "Active working time, credited only while the Grid Professor task pane is open.",
      points: [
         "Active time is not the time the file was open. It counts work, so it pauses when you stop and resumes when you start again.",
         "If this reads zero after a session, the pane was probably closed. Nothing recovers that time.",
      ],
   },

   "student:xp": {
      title: "Your XP",
      blurb: "Your level, streak and medals, where every point came from, and the leaderboards for your Game Launcher classes.",
      points: [
         "Only a clean clear pays: the tab cleared, every check passed, the guide not opened. Where my XP came from lists the tabs that paid nothing and why.",
         "How XP works is written from the live rules, so its numbers are always the ones in use.",
      ],
   },

   "student:training": {
      title: "Practice material",
      blurb: "Published drills and walkthroughs, the same for everyone, not tied to a class.",
      points: [
         "Nothing here is graded and nothing is due.",
      ],
   },

   "student:class-boards": {
      title: "Class standings",
      blurb: "Where your instructor has switched on a visible board for a class.",
      points: [
         "Boards show nicknames rather than names wherever a class or a projector can see them.",
      ],
   },
};
